import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Link } from "react-router-dom";
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  ArrowClockwise, ArrowLeft, ChartBar, CheckCircle, ClipboardText, DownloadSimple, FileArrowUp, IdentificationCard, MagnifyingGlass,
  PencilSimple, Spinner, Trash, UploadSimple, UsersThree, Warning, XCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtError } from "../lib/api";
import { uploadOmrBatch } from "../lib/omrUpload";
import { Button } from "../components/ui/button";
import PsychometricsPanel from "../components/PsychometricsPanel";
import AcademicDomainChart from "../components/AcademicDomainChart";
import EnamedProfilePanel from "../components/EnamedProfilePanel";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const scoreTone = (score) => (
  score >= 70 ? "text-[hsl(var(--success))]"
    : score >= 50 ? "text-[hsl(var(--warning))]"
      : "text-destructive"
);

const StatBox = ({ label, value, hint }) => (
  <div className="border border-border bg-card p-5 min-h-[118px] flex flex-col justify-between">
    <div className="label-eyebrow">{label}</div>
    <div>
      <div className="font-display text-4xl font-black tracking-tight leading-none">{value}</div>
      {hint && <div className="text-xs text-muted-foreground font-mono-data mt-2">{hint}</div>}
    </div>
  </div>
);

function GlobalExamPerformancePanel({ exam }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get(`/exams/${exam.id}/global-performance`);
      setData(response.data);
    } catch (requestError) {
      const message = fmtError(requestError);
      setError(message);
      toast.error("Falha ao carregar o comparativo global: " + message);
    } finally {
      setLoading(false);
    }
  }, [exam.id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="border border-border bg-card p-8 text-sm text-muted-foreground"><Spinner className="inline mr-2 animate-spin" />Calculando desempenho por etapa e área…</div>;
  if (error) return <div className="border border-destructive bg-destructive/5 p-5 text-sm"><b>Não foi possível carregar o comparativo.</b><div className="mt-1 text-muted-foreground">{error}</div><Button variant="outline" className="rounded-none mt-3" onClick={load}>Tentar novamente</Button></div>;
  const stages = data?.stages || [];
  const summary = data?.summary || {};
  return <section className="space-y-5" data-testid="global-exam-performance">
    <div className="border border-primary bg-primary/5 p-5 flex flex-col md:flex-row md:items-end justify-between gap-4">
      <div><div className="label-eyebrow">prova global · comparação institucional</div><h2 className="font-display text-3xl font-black mt-1">Desempenho por etapa, turma e área.</h2><p className="text-sm text-muted-foreground mt-2">Cada aluno entra uma vez: o cartão válido mais recente desta prova.</p></div>
      <div className="font-mono-data text-right"><div className="text-4xl font-black text-primary">{Number(summary.average_score || 0).toFixed(1)}%</div><div className="text-xs text-muted-foreground">{summary.participants || 0} aluno(s) · {summary.cards || 0} cartão(ões)</div></div>
    </div>
    {!stages.length ? <div className="border border-border bg-card p-8 text-sm text-muted-foreground">Ainda não há cartões com turma identificada para comparar nesta prova.</div> : <>
      <div className="border border-border bg-card p-5 h-[330px]">
        <div className="label-eyebrow mb-3">média global por etapa</div>
        <ResponsiveContainer width="100%" height="92%"><BarChart data={stages}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis domain={[0, 100]} unit="%" /><Tooltip formatter={(value) => `${Number(value).toFixed(1)}%`} /><Bar dataKey="average_score" name="Média de acertos" fill="hsl(var(--primary))" /></BarChart></ResponsiveContainer>
      </div>
      <div className="grid gap-4">
        {stages.map((stage) => <article key={stage.id} className="border border-border bg-card">
          <div className="p-5 border-b border-border flex flex-col md:flex-row md:items-center justify-between gap-3"><div><div className="label-eyebrow">etapa</div><h3 className="font-display text-2xl font-black">{stage.name}</h3></div><div className="font-mono-data text-right"><b className={scoreTone(stage.average_score)}>{Number(stage.average_score).toFixed(1)}%</b><div className="text-xs text-muted-foreground">{stage.participants} aluno(s) · {stage.cards} cartão(ões)</div></div></div>
          <div className="p-5 grid grid-cols-1 xl:grid-cols-2 gap-5">
            <div><div className="label-eyebrow mb-2">turmas</div><div className="grid gap-2">{(stage.classes || []).map((classItem) => <div key={classItem.id} className="border border-border px-3 py-2 flex justify-between gap-3"><span className="text-sm">{classItem.name}</span><span className={`font-mono-data font-bold ${scoreTone(classItem.average_score)}`}>{Number(classItem.average_score).toFixed(1)}% <small className="text-muted-foreground">({classItem.participants})</small></span></div>)}</div></div>
            <div><div className="label-eyebrow mb-2">áreas nesta etapa</div>{!(stage.areas || []).length ? <div className="text-sm text-muted-foreground">Esta prova não possui taxonomia de áreas nas questões.</div> : <div className="grid gap-2">{stage.areas.map((area) => <div key={area.name} className="border border-border px-3 py-2 flex justify-between gap-3"><span className="text-sm truncate">{area.name}</span><span className={`font-mono-data font-bold ${scoreTone(area.hit_percentage)}`}>{Number(area.hit_percentage).toFixed(1)}% <small className="text-muted-foreground">({area.responses})</small></span></div>)}</div>}</div>
          </div>
        </article>)}
      </div>
    </>}
  </section>;
}

const keyChangeValue = (answer, annulled) => {
  if (annulled) return "ANULADA";
  return Array.isArray(answer) ? answer.join(" + ") : (answer || "—");
};

function QuestionKeyChanges({ exam, onExamChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/exams/${exam.id}/question-key-changes`);
      setItems(response.data.items || []);
    } catch (error) {
      toast.error("Falha ao consultar mudanças de gabarito: " + fmtError(error));
    } finally { setLoading(false); }
  }, [exam.id]);

  useEffect(() => { load(); }, [load]);

  const decide = async (item, decision) => {
    const action = decision === "accept" ? "aplicar esta mudança" : "manter o gabarito original";
    if (!window.confirm(`Deseja ${action} na questão ${item.target_position} desta prova?`)) return;
    setDeciding(item.id);
    try {
      await api.post(`/exams/${exam.id}/question-key-changes/${item.id}/decision`, { decision });
      toast.success(decision === "accept" ? "Mudança aplicada somente nesta prova" : "Gabarito histórico preservado nesta prova");
      setItems((current) => current.filter((value) => value.id !== item.id));
      if (decision === "accept" && onExamChanged) {
        const refreshed = await api.get(`/exams/${exam.id}`);
        onExamChanged(refreshed.data);
      }
    } catch (error) {
      toast.error("Falha ao registrar decisão: " + fmtError(error));
    } finally { setDeciding(""); }
  };

  if (loading && Number(exam.pending_question_key_changes || 0) > 0) {
    return <div className="border border-destructive bg-destructive/5 p-4 mb-5 text-sm"><Spinner className="inline mr-2 animate-spin"/>Carregando mudanças de gabarito pendentes…</div>;
  }
  if (!items.length) return null;
  return <section className="border-2 border-destructive bg-destructive/5 mb-6" data-testid="question-key-change-notice">
    <div className="p-5 border-b border-destructive/30">
      <div className="label-eyebrow text-destructive">decisão pedagógica necessária</div>
      <h2 className="font-display text-2xl font-black mt-1">Mudança de gabarito em questão reutilizada</h2>
      <p className="text-sm text-muted-foreground mt-2">As notas desta prova permanecem como estavam até você aceitar. Cada decisão vale somente para esta prova e fica registrada.</p>
    </div>
    <div className="divide-y divide-destructive/20">
      {items.map((item) => <div key={item.id} className="p-5 flex flex-col xl:flex-row xl:items-center gap-4 justify-between">
        <div className="min-w-0">
          <div className="font-mono-data font-bold">Q{item.target_position} · {item.question_code || item.question_id}</div>
          <div className="text-sm mt-1">Mudança originada em <b>{item.source_exam_name || "outra prova"}</b> (questão {item.source_position}).</div>
          <div className="flex flex-wrap gap-2 mt-3 text-xs font-mono-data">
            <span className="border border-border bg-card px-2 py-1">atual: {keyChangeValue(item.previous_answer, item.previous_annulled)}</span>
            <span className="border border-destructive bg-card px-2 py-1 text-destructive">proposta: {keyChangeValue(item.proposed_answer, item.proposed_annulled)}</span>
            <span className="border border-border bg-card px-2 py-1">{item.target_correction_count || 0} correção(ões) existentes</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button variant="outline" className="rounded-none" disabled={deciding === item.id} onClick={() => decide(item, "keep")}>Manter original</Button>
          <Button className="rounded-none" disabled={deciding === item.id} onClick={() => decide(item, "accept")}>{deciding === item.id && <Spinner className="mr-2 animate-spin"/>}Assumir mudança</Button>
        </div>
      </div>)}
    </div>
  </section>;
}

function UploadPanel({ exam, classOptions = [], onFinished }) {
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [batchResults, setBatchResults] = useState([]);
  const requiresBatchClass = Boolean(exam.historical_unassigned);
  const [targetClassId, setTargetClassId] = useState(
    requiresBatchClass
      ? (classOptions.length === 1 ? classOptions[0].id : "")
      : (classOptions.length === 1 ? classOptions[0].id : "auto"),
  );
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewReport, setReviewReport] = useState(null);
  const [resolvingDuplicate, setResolvingDuplicate] = useState("");
  const validQuestions = Object.values(exam.answers || {}).filter(Boolean).length;

  useEffect(() => {
    setTargetClassId((current) => {
      if (requiresBatchClass) {
        if (classOptions.some((item) => item.id === current)) return current;
        return classOptions.length === 1 ? classOptions[0].id : "";
      }
      return classOptions.length === 1 ? classOptions[0].id : "auto";
    });
  }, [exam.id, requiresBatchClass, classOptions]);

  const resolveDuplicate = async (correctionId, action) => {
    setResolvingDuplicate(correctionId);
    try {
      await api.post(`/omr/duplicates/${correctionId}/resolve`, { action });
      setReviewReport((current) => ({
        ...current,
        duplicates: (current?.duplicates || []).filter((item) => item.correction_id !== correctionId),
        counts: {
          ...(current?.counts || {}),
          duplicates: Math.max(0, (current?.counts?.duplicates || 1) - 1),
        },
      }));
      toast.success(
        action === "keep_existing" ? "Correção antiga mantida"
          : action === "keep_new" ? "Nova correção mantida"
            : "As duas correções foram mantidas",
      );
      await onFinished();
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setResolvingDuplicate("");
    }
  };

  const onDrop = useCallback(async (accepted) => {
    if (!accepted.length || processing) return;
    if (!validQuestions) {
      toast.error("Cadastre o gabarito antes de enviar os cartões");
      return;
    }
    if (requiresBatchClass && !targetClassId) {
      toast.info("Os cartões serão processados sem turma pré-selecionada. Você poderá vincular os nomes manualmente na revisão.");
    }

    setProcessing(true);
    setProgress(0);
    setBatchResults([]);
    setReviewReport(null);
    let jobId;
    try {
      const response = await uploadOmrBatch(accepted, {
        examId: exam.id,
        classId: targetClassId && targetClassId !== "auto" ? targetClassId : null,
        onProgress: setProgress,
      });
      jobId = response.data.job_id;
      if (!jobId) throw new Error("Servidor não retornou o identificador do processamento");
    } catch (error) {
      toast.error("Falha no envio: " + fmtError(error));
      setProcessing(false);
      return;
    }

    setProgress(0);
    try {
      while (true) {
        await new Promise((resolve) => setTimeout(resolve, 1800));
        const response = await api.get(`/omr/jobs/${jobId}`);
        const job = response.data;
        const successful = job.results || [];
        const errors = (job.errors || []).map((item) => ({
          filename: item.filename,
          error: item.error,
        }));
        setBatchResults([...successful, ...errors]);
        const denominator = Math.max(job.total_pages || job.total_files || 1, 1);
        setProgress(Math.min(100, Math.round(((job.processed_pages || 0) / denominator) * 100)));

        if (job.status === "completed" || job.status === "failed") {
          if (job.status === "failed") {
            toast.error("Processamento interrompido: " + (job.error || "erro desconhecido"));
          } else {
            const pendingDuplicates = job.review_report?.counts?.duplicates || 0;
            const pendingStudents = job.review_report?.counts?.student_issues || 0;
            toast.success(
              `${successful.length} cartão(ões) processado(s)`
              + `${errors.length ? ` · ${errors.length} com erro` : ""}`
              + `${pendingDuplicates ? ` · ${pendingDuplicates} duplicado(s) aguardando decisão` : ""}`
              + `${pendingStudents ? ` · ${pendingStudents} CPF(s) sem aluno` : ""}`,
            );
            setReviewReport(job.review_report || { counts: {} });
            setReviewOpen(false);
            await onFinished(job.review_report || { counts: {} });
          }
          break;
        }
      }
    } catch (error) {
      toast.error("Falha ao acompanhar o processamento: " + fmtError(error));
    } finally {
      setProcessing(false);
    }
  }, [exam.id, onFinished, processing, requiresBatchClass, targetClassId, validQuestions]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".png", ".jpg", ".jpeg", ".webp"],
    },
    multiple: true,
    // A turma acelera a vinculação por OCR nas provas históricas, mas nunca
    // pode impedir o seletor de arquivos. Sem essa escolha, a revisão manual
    // continua disponível com a lista completa de alunos autorizados.
    disabled: processing || !validQuestions,
  });

  const counts = reviewReport?.counts || {};
  const totalReview = Object.values(counts).reduce((sum, value) => sum + Number(value || 0), 0);

  return (
    <div className="space-y-5">
      {!validQuestions && (
        <div className="border border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/5 p-4 flex items-start gap-3">
          <Warning size={20} weight="fill" className="text-[hsl(var(--warning))] shrink-0 mt-0.5" />
          <div>
            <div className="font-mono-data font-semibold text-sm">Gabarito ainda vazio</div>
            <div className="text-sm text-muted-foreground mt-1">
              Preencha ou importe o gabarito para que o sistema calcule os acertos de cada aluno.
            </div>
          </div>
        </div>
      )}

      {(requiresBatchClass || classOptions.length > 1) && (
        <div className="border border-border bg-card p-4 max-w-xl">
          <div className="label-eyebrow mb-1">turma dos cartões deste lote {requiresBatchClass ? "· opcional" : ""}</div>
          <Select value={targetClassId} onValueChange={setTargetClassId} disabled={processing || !classOptions.length}>
            <SelectTrigger data-testid="exam-batch-class-select" className="rounded-none">
              <SelectValue placeholder={requiresBatchClass ? "Selecione a turma deste lote" : undefined} />
            </SelectTrigger>
            <SelectContent>
              {!requiresBatchClass && <SelectItem value="auto">Identificar automaticamente pela matrícula do aluno</SelectItem>}
              {classOptions.map((item) => (
                <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-xs text-muted-foreground mt-2">
            {requiresBatchClass
              ? classOptions.length
                ? "Opcional: a turma ajuda a conferir o nome lido no cartão. Sem escolhê-la, você ainda pode enviar os cartões e vincular os alunos na tela de revisão. A prova histórica não será vinculada permanentemente a ela."
                : "Nenhuma turma está disponível para você. Ainda assim, você pode enviar os cartões e fazer o vínculo manual na revisão."
              : "Escolha uma turma quando o mesmo aluno puder estar matriculado em mais de uma das turmas desta prova."}
          </div>
        </div>
      )}

      <div
        {...getRootProps()}
        data-testid="exam-card-upload-dropzone"
        className={`border-2 border-dashed p-12 text-center transition-colors ${
          processing || !validQuestions
            ? "border-border bg-muted/20 cursor-not-allowed opacity-70"
            : isDragActive
              ? "border-primary bg-primary/5 cursor-copy"
              : "border-border hover:border-primary cursor-pointer"
        }`}
      >
        <input {...getInputProps()} data-testid="exam-card-upload-input" />
        {processing ? (
          <Spinner size={54} className="mx-auto text-primary animate-spin" />
        ) : (
          <FileArrowUp size={54} weight="duotone" className="mx-auto text-primary" />
        )}
        <div className="font-display text-2xl font-black tracking-tight mt-4">
          {processing ? "Corrigindo cartões…" : "Envie os cartões desta prova"}
        </div>
        <div className="text-sm text-muted-foreground mt-2">
          Arraste PDFs ou imagens para esta área, ou clique para selecionar. Cada página de PDF será tratada como um cartão.
        </div>
        <div className="label-eyebrow mt-4">prova vinculada automaticamente · {exam.name}</div>
      </div>

      {processing && (
        <div className="border border-border bg-card p-4">
          <div className="flex justify-between text-xs font-mono-data mb-2">
            <span>processamento OMR</span>
            <span>{progress}%</span>
          </div>
          <div className="h-2 bg-muted overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      {batchResults.length > 0 && (
        <div className="border border-border bg-card">
          <div className="px-5 py-3 border-b border-border label-eyebrow">último lote</div>
          <div className="divide-y divide-border">
            {batchResults.map((result, index) => (
              <div key={`${result.id || result.filename}-${index}`} className="px-5 py-3 flex items-center gap-3">
                {result.error ? (
                  <XCircle size={18} weight="fill" className="text-destructive shrink-0" />
                ) : result.duplicate_status === "pending" || !result.student_id || result.cpf?.includes("?") ? (
                  <Warning size={18} weight="fill" className="text-[hsl(var(--warning))] shrink-0" />
                ) : (
                  <CheckCircle size={18} weight="fill" className="text-[hsl(var(--success))] shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{result.filename}</div>
                  <div className="text-xs text-muted-foreground font-mono-data">
                    {result.error ? result.error : `CPF ${result.cpf || "não reconhecido"}`}
                    {result.auto_rotation?.selected_degrees === 180 ? " · corrigido após giro de 180°" : ""}
                    {result.duplicate_status === "pending" ? " · duplicidade pendente" : ""}
                    {!result.student_id ? " · aluno não vinculado" : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="rounded-none max-w-5xl max-h-[92vh] overflow-y-auto" aria-describedby="batch-review-desc">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-black tracking-tight">Relatório de revisão do lote</DialogTitle>
          </DialogHeader>
          <div id="batch-review-desc" className="text-sm text-muted-foreground">
            {totalReview
              ? "Revise as ocorrências abaixo. Cartões duplicados não entram no resultado oficial até sua decisão."
              : "Nenhuma pendência de reconhecimento foi encontrada neste lote."}
          </div>

          {!!reviewReport?.processing_errors?.length && (
            <section className="border border-destructive/50 bg-destructive/5">
              <div className="px-4 py-3 border-b border-destructive/30 font-mono-data font-bold text-destructive">Falhas de processamento</div>
              <div className="divide-y divide-border">
                {reviewReport.processing_errors.map((item, index) => (
                  <div key={`${item.filename}-${index}`} className="p-4 text-sm">
                    <div className="font-semibold">{item.filename}</div>
                    <div className="text-destructive mt-1">{item.error}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {!!reviewReport?.objective_issues?.length && (
            <section className="border border-border">
              <div className="px-4 py-3 border-b border-border font-mono-data font-bold">Questões objetivas em branco</div>
              <div className="divide-y divide-border">
                {reviewReport.objective_issues.map((item) => (
                  <div key={item.correction_id} className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <div className="font-semibold">{item.filename}</div>
                      <div className="text-xs text-muted-foreground font-mono-data mt-1">
                        CPF {item.cpf || "não reconhecido"} · {item.blank_valid} em branco · {item.misses} divergente(s)
                        {item.rotation_degrees === 180 ? " · leitura escolhida a 180°" : ""}
                      </div>
                    </div>
                    <Link to={`/correction/${item.correction_id}`} className="text-xs font-mono-data uppercase tracking-widest text-primary hover:underline">Revisar cartão</Link>
                  </div>
                ))}
              </div>
            </section>
          )}

          {!!reviewReport?.perception_issues?.length && (
            <section className="border border-border">
              <div className="px-4 py-3 border-b border-border font-mono-data font-bold">Percepção não reconhecida</div>
              <div className="divide-y divide-border">
                {reviewReport.perception_issues.map((item) => (
                  <div key={item.correction_id} className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <div className="font-semibold">{item.filename}</div>
                      <div className="text-xs text-muted-foreground font-mono-data mt-1">
                        {item.unrecognized.join(", ")} sem reconhecimento
                      </div>
                    </div>
                    <Link to={`/correction/${item.correction_id}`} className="text-xs font-mono-data uppercase tracking-widest text-primary hover:underline">Revisar cartão</Link>
                  </div>
                ))}
              </div>
            </section>
          )}

          {!!reviewReport?.class_issues?.length && (
            <section className="border border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/5">
              <div className="px-4 py-3 border-b border-border font-mono-data font-bold">Turma não identificada</div>
              {reviewReport.class_issues.map((item) => (
                <div key={item.correction_id} className="p-4 text-sm border-b border-border last:border-b-0">
                  <div className="font-semibold">{item.filename}</div>
                  <div className="text-muted-foreground mt-1">{item.message}</div>
                </div>
              ))}
            </section>
          )}

          {!!reviewReport?.duplicates?.length && (
            <section className="border border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/5">
              <div className="px-4 py-3 border-b border-border font-mono-data font-bold">Cartões escaneados mais de uma vez</div>
              <div className="divide-y divide-border">
                {reviewReport.duplicates.map((item) => (
                  <div key={item.correction_id} className="p-5">
                    <div className="font-semibold">{item.filename}</div>
                    <div className="text-sm mt-2">
                      {item.identical
                        ? "A nova leitura é igual à correção anterior."
                        : `Foram encontradas ${item.objective_difference_count || 0} diferença(s) objetivas e ${item.perception_difference_count || 0} na percepção.`}
                    </div>
                    {!item.identical && (
                      <div className="text-xs text-muted-foreground font-mono-data mt-2">
                        Antiga: {Number(item.old_score?.score || 0).toFixed(1)}% · Nova: {Number(item.new_score?.score || 0).toFixed(1)}%
                        {item.objective_differences?.length ? ` · Questões: ${item.objective_differences.join(", ")}` : ""}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 mt-4">
                      <Button variant="outline" className="rounded-none" disabled={resolvingDuplicate === item.correction_id}
                        onClick={() => resolveDuplicate(item.correction_id, "keep_existing")}>Manter antiga</Button>
                      <Button variant="outline" className="rounded-none" disabled={resolvingDuplicate === item.correction_id}
                        onClick={() => resolveDuplicate(item.correction_id, "keep_new")}>Manter nova</Button>
                      <Button className="rounded-none" disabled={resolvingDuplicate === item.correction_id}
                        onClick={() => resolveDuplicate(item.correction_id, "keep_both")}>Manter ambas</Button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <DialogFooter>
            <Button className="rounded-none" onClick={() => setReviewOpen(false)}>Fechar relatório</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const identityCropSrc = (value) => {
  if (typeof value === "string") {
    const source = value.trim();
    return /^(?:data:image\/|https?:\/\/|\/)/i.test(source) ? source : "";
  }
  if (!value || typeof value !== "object") return "";
  const direct = value.image_data_uri || value.data_url || value.dataUrl || value.url || value.src || value.image_url || value.imageUrl;
  if (typeof direct === "string" && direct.trim()) return direct.trim();
  const encoded = value.base64 || value.png_b64 || value.preview_b64 || value.data;
  if (typeof encoded !== "string" || !encoded.trim()) return "";
  if (/^data:image\//i.test(encoded.trim())) return encoded.trim();
  return `data:${String(value.mime_type || value.mime || "image/png")};base64,${encoded.trim().replace(/^base64,/i, "")}`;
};

// The visual identity review intentionally fetches the complete protected
// roster once, then filters locally. Do not restore a silent 500-name cutoff.
const IDENTITY_REVIEW_ROSTER_LIMIT = 5000;

const candidateSimilarityPercent = (student) => {
  const raw = Number(
    student?.name_match_percent
    ?? student?.name_similarity_percent
    ?? student?.match_percent
    ?? student?.name_match_similarity
    ?? student?.name_similarity
    ?? student?.match_score,
  );
  if (!Number.isFinite(raw) || raw < 0) return null;
  return Math.min(100, raw <= 1 ? raw * 100 : raw);
};

const normalizeIdentitySearch = (value) => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLocaleLowerCase("pt-BR")
  .trim();

function StudentIdentityReviewWorkspace({ items = [], selectedCorrectionId = "", onResolved }) {
  const [selectedId, setSelectedId] = useState("");
  const [cardSearch, setCardSearch] = useState("");
  const [candidateSearch, setCandidateSearch] = useState("");
  const [selectedCandidateClassIds, setSelectedCandidateClassIds] = useState([]);
  const [candidateContext, setCandidateContext] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [candidateCount, setCandidateCount] = useState(0);
  const [identityLoadedFor, setIdentityLoadedFor] = useState("");
  const [loadingIdentity, setLoadingIdentity] = useState(false);
  const [pendingOperations, setPendingOperations] = useState(() => ({}));
  const [submittingBatch, setSubmittingBatch] = useState(false);
  const [cropUnavailable, setCropUnavailable] = useState(false);
  const [resolvedIds, setResolvedIds] = useState(() => new Set());
  // A review batch can have hundreds of cards. Keep each protected crop and
  // authorized roster in memory for the current page so revisiting a card
  // never repeats the OCR/candidate request. The search below runs locally.
  const identityCache = useRef(new Map());
  const identityRequests = useRef(new Map());
  const identityPrefetchRequests = useRef(new Map());
  const activeIdentityCorrectionId = useRef("");

  useEffect(() => {
    setResolvedIds((current) => {
      const currentIds = new Set(items.map((item) => item.correction_id));
      const retained = [...current].filter((id) => currentIds.has(id));
      return retained.length === current.size ? current : new Set(retained);
    });
  }, [items]);

  // A queued action is deliberately kept in the browser until the operator
  // presses "Enviar altera\u00e7\u00f5es".  If the server refreshes this exam while the
  // workspace is open, discard only operations whose cards disappeared.
  useEffect(() => {
    setPendingOperations((current) => {
      const itemIds = new Set(items.map((item) => item.correction_id));
      const next = Object.fromEntries(
        Object.entries(current).filter(([correctionId]) => itemIds.has(correctionId)),
      );
      return Object.keys(next).length === Object.keys(current).length ? current : next;
    });
  }, [items]);

  const pendingOperationList = useMemo(
    () => Object.values(pendingOperations),
    [pendingOperations],
  );
  const pendingIdSet = useMemo(
    () => new Set(pendingOperationList.map((operation) => operation.correction_id)),
    [pendingOperationList],
  );

  const availableItems = useMemo(
    () => items.filter((item) => !resolvedIds.has(item.correction_id) && !pendingIdSet.has(item.correction_id)),
    [items, pendingIdSet, resolvedIds],
  );

  const pendingItems = useMemo(
    () => items.filter((item) => !resolvedIds.has(item.correction_id) && pendingIdSet.has(item.correction_id)),
    [items, pendingIdSet, resolvedIds],
  );

  // Active cards remain first so the next decision always moves forward.
  // Pending cards are still visible below, where they can be cancelled before
  // the one final batch submission.
  const reviewItems = useMemo(
    () => [...availableItems, ...pendingItems],
    [availableItems, pendingItems],
  );

  const visibleItems = useMemo(() => {
    const term = cardSearch.trim().toLocaleLowerCase("pt-BR");
    if (!term) return reviewItems;
    const cpfTerm = term.replace(/\D/g, "");
    return reviewItems.filter((item) => (
      String(item.filename || "").toLocaleLowerCase("pt-BR").includes(term)
      || String(item.recognized_name || item.student_name_ocr?.text || "").toLocaleLowerCase("pt-BR").includes(term)
      || (cpfTerm && String(item.cpf || "").replace(/\D/g, "").includes(cpfTerm))
    ));
  }, [cardSearch, reviewItems]);

  const selected = reviewItems.find((item) => item.correction_id === selectedId) || null;
  const selectedOperation = selected ? pendingOperations[selected.correction_id] : null;
  const selectedIsPending = Boolean(selectedOperation);

  useEffect(() => {
    if (selectedCorrectionId && reviewItems.some((item) => item.correction_id === selectedCorrectionId)) {
      setSelectedId(selectedCorrectionId);
    }
  }, [reviewItems, selectedCorrectionId]);

  const applyIdentitySnapshot = useCallback((correctionId, snapshot) => {
    if (activeIdentityCorrectionId.current !== correctionId) return;
    setCandidateContext(snapshot.context || null);
    setCandidates(Array.isArray(snapshot.candidates) ? snapshot.candidates : []);
    setCandidateCount(Number(snapshot.total || 0));
    setIdentityLoadedFor(correctionId);
    setCropUnavailable(false);
    setLoadingIdentity(false);
  }, []);

  const toIdentitySnapshot = useCallback((payload, isVisualReview = false) => {
    const source = payload && typeof payload === "object" ? payload : {};
    const rows = Array.isArray(source)
      ? source
      : (source.candidates || source.students || source.results || source.items || []);
    return {
      context: isVisualReview ? { ...source, identity_visual_review: true } : source,
      candidates: Array.isArray(rows) ? rows : [],
      total: Number(source.total ?? source.count ?? source.returned ?? rows?.length ?? 0),
    };
  }, []);

  const loadIdentity = useCallback(async (correctionId, { force = false } = {}) => {
    if (!correctionId) return;
    const cached = identityCache.current.get(correctionId);
    if (cached && !force) {
      applyIdentitySnapshot(correctionId, cached);
      return cached;
    }

    if (activeIdentityCorrectionId.current === correctionId) {
      setLoadingIdentity(true);
      setCropUnavailable(false);
    }

    // React may re-run effects in development, and the user may change cards
    // while a request is in flight. Share that one request rather than asking
    // the server to rebuild the crop/list a second time.
    let request = identityRequests.current.get(correctionId);
    if (!request) {
      request = (async () => {
        const params = { limit: IDENTITY_REVIEW_ROSTER_LIMIT };
        let response;
        let isVisualReview = false;
        try {
          // The protected endpoint returns the layout-specific name crop and
          // the whole authorized roster together. Filtering happens locally.
          response = await api.get(`/omr/corrections/${correctionId}/student-name-review`, { params });
          isVisualReview = true;
        } catch (reviewError) {
          if (![404, 409].includes(reviewError?.response?.status)) throw reviewError;
          response = await api.get(`/omr/corrections/${correctionId}/student-candidates`, { params });
        }
        return toIdentitySnapshot(response.data || {}, isVisualReview);
      })();
      identityRequests.current.set(correctionId, request);
    }

    try {
      const snapshot = await request;
      identityCache.current.set(correctionId, snapshot);
      applyIdentitySnapshot(correctionId, snapshot);
      return snapshot;
    } catch (error) {
      if (activeIdentityCorrectionId.current === correctionId) {
        if (cached) {
          // A manual refresh may fail transiently. Keep the last good crop and
          // roster usable instead of throwing the operator back to an empty pane.
          applyIdentitySnapshot(correctionId, cached);
        } else {
          setCandidateContext(null);
          setCandidates([]);
          setCandidateCount(0);
          // Mark this card as settled so an unavailable crop/list presents the
          // retry button instead of leaving the panel in a permanent spinner.
          setIdentityLoadedFor(correctionId);
          setLoadingIdentity(false);
        }
        toast.error("Falha ao carregar alunos: " + fmtError(error));
      }
      return null;
    } finally {
      if (identityRequests.current.get(correctionId) === request) {
        identityRequests.current.delete(correctionId);
      }
    }
  }, [applyIdentitySnapshot, toIdentitySnapshot]);

  useEffect(() => {
    if (!selected?.correction_id) {
      activeIdentityCorrectionId.current = "";
      setCandidateContext(null);
      setCandidates([]);
      setCandidateCount(0);
      setIdentityLoadedFor("");
      setCandidateSearch("");
      setLoadingIdentity(false);
      return undefined;
    }
    const correctionId = selected.correction_id;
    activeIdentityCorrectionId.current = correctionId;
    setCandidateSearch("");
    const cached = identityCache.current.get(correctionId);
    if (cached) {
      applyIdentitySnapshot(correctionId, cached);
      return undefined;
    }
    setCandidateContext(null);
    setCandidates([]);
    setCandidateCount(0);
    setIdentityLoadedFor("");
    setCropUnavailable(false);
    setLoadingIdentity(true);
    // Active cards are owned by the rolling batch below.  Do not also make a
    // one-card GET here: that would duplicate card 1 while the API is already
    // preparing cards 1--10. Pending cards are outside that window and still
    // use the one-card loader when reopened for cancellation/inspection.
    if (availableItems.some((item) => item.correction_id === correctionId)) {
      return undefined;
    }
    loadIdentity(correctionId);
    return undefined;
  }, [applyIdentitySnapshot, availableItems, loadIdentity, selected?.correction_id]);

  const prefetchIdentityWindow = useCallback(async (correctionIds) => {
    const missingIds = correctionIds.filter(
      (correctionId) => (
        !identityCache.current.has(correctionId)
        && !identityPrefetchRequests.current.has(correctionId)
      ),
    );
    if (!missingIds.length) return;
    let request;
    try {
      // One batch lets the API share its roster/cache work while it prepares
      // up to ten independent crops in parallel.
      request = api.post("/omr/corrections/student-name-review/prefetch", {
        correction_ids: missingIds,
        limit: IDENTITY_REVIEW_ROSTER_LIMIT,
      });
      missingIds.forEach((correctionId) => {
        identityPrefetchRequests.current.set(correctionId, request);
      });
      const response = await request;
      const rows = Array.isArray(response.data?.items) ? response.data.items : [];
      const sharedRosters = response.data?.shared_rosters && typeof response.data.shared_rosters === "object"
        ? response.data.shared_rosters
        : {};
      rows.forEach((row) => {
        if (!row?.ok || !row?.correction_id) return;
        const sharedRoster = row?.roster_key ? sharedRosters[row.roster_key] : null;
        const hydratedRow = sharedRoster
          ? {
            ...row,
            candidates: Array.isArray(sharedRoster.candidates) ? sharedRoster.candidates : [],
            total: Number(sharedRoster.total ?? row.roster_total ?? row.returned ?? 0),
          }
          : row;
        const snapshot = toIdentitySnapshot(hydratedRow, true);
        identityCache.current.set(row.correction_id, snapshot);
        applyIdentitySnapshot(row.correction_id, snapshot);
      });
      // A partial prefetch result must not strand the visible card in a
      // permanent loading state. Only that card gets the safe one-card
      // fallback; other failed rows wait for a later rolling window/retry.
      const activeCorrectionId = activeIdentityCorrectionId.current;
      if (
        activeCorrectionId
        && missingIds.includes(activeCorrectionId)
        && !identityCache.current.has(activeCorrectionId)
      ) {
        void loadIdentity(activeCorrectionId);
      }
    } catch (error) {
      // Older backend during a rolling deploy has no prefetch route. Keep the
      // screen usable by falling back to individual requests only for that
      // compatibility case, never by retrying all ten after a real error.
      if ([404, 405, 501].includes(error?.response?.status)) {
        await Promise.allSettled(missingIds.map((correctionId) => loadIdentity(correctionId)));
        return;
      }
      // If the one batched warm-up itself has a transient failure, retry only
      // the card currently being viewed. This clears its spinner without
      // generating a second burst of ten database/OCR requests.
      const activeCorrectionId = activeIdentityCorrectionId.current;
      if (activeCorrectionId && missingIds.includes(activeCorrectionId)) {
        void loadIdentity(activeCorrectionId);
      }
    } finally {
      missingIds.forEach((correctionId) => {
        if (identityPrefetchRequests.current.get(correctionId) === request) {
          identityPrefetchRequests.current.delete(correctionId);
        }
      });
    }
  }, [applyIdentitySnapshot, loadIdentity, toIdentitySnapshot]);

  // Keep a rolling window of ten cards ready. Opening card 1 warms 1--10;
  // after card 1 is queued, the window advances to 2--11, and so forth.
  // This is intentionally bounded at ten to avoid an unbounded burst.
  useEffect(() => {
    if (!availableItems.length) return undefined;
    const selectedIndex = availableItems.findIndex(
      (item) => item.correction_id === selected?.correction_id,
    );
    const start = selectedIndex >= 0 ? selectedIndex : 0;
    const correctionIds = availableItems
      .slice(start, start + 10)
      .map((item) => item.correction_id)
      .filter(Boolean);
    if (!correctionIds.length) return undefined;
    void prefetchIdentityWindow(correctionIds);
    return undefined;
  }, [availableItems, prefetchIdentityWindow, selected?.correction_id]);

  const chooseCard = (item) => {
    if (!item?.correction_id) return;
    setSelectedId(item.correction_id);
  };

  const nextAvailableAfter = useCallback((correctionId) => {
    const index = availableItems.findIndex((item) => item.correction_id === correctionId);
    return availableItems[index + 1] || availableItems[index - 1] || null;
  }, [availableItems]);

  const queueOperation = useCallback((operation) => {
    if (!selected?.correction_id || submittingBatch) return;
    const correctionId = selected.correction_id;
    const nextItem = nextAvailableAfter(correctionId);
    setPendingOperations((current) => ({
      ...current,
      [correctionId]: {
        correction_id: correctionId,
        ...operation,
        queued_at: new Date().toISOString(),
      },
    }));
    setCandidateSearch("");
    setSelectedId(nextItem?.correction_id || "");
  }, [nextAvailableAfter, selected?.correction_id, submittingBatch]);

  const linkCandidate = (student) => {
    if (!selected?.correction_id || !student?.id) return;
    queueOperation({
      action: "link",
      student_id: student.id,
      student_name: student.name || "Aluno selecionado",
    });
    toast.success(`Vínculo com ${student.name || "o aluno"} preparado. Envie todas as alterações ao final.`);
  };

  const deleteSelectedCard = () => {
    if (!selected?.correction_id) return;
    const filename = selected.filename || "este cartão";
    if (!window.confirm(`Preparar a exclusão de ${filename}? Ela só será aplicada quando você enviar as alterações.`)) return;
    queueOperation({ action: "delete", filename });
    toast.success("Exclusão preparada. O cartão continuará visível até o envio final.");
  };

  const ignoreSelectedCard = () => {
    if (!selected?.correction_id) return;
    if (!window.confirm("Manter este cartão sem aluno vinculado? A decisão só será aplicada no envio final.")) return;
    queueOperation({ action: "ignore" });
    toast.success("Cartão marcado para permanecer sem aluno. Envie as alterações ao final.");
  };

  const cancelPendingOperation = (correctionId = selected?.correction_id) => {
    if (!correctionId || submittingBatch) return;
    setPendingOperations((current) => {
      if (!current[correctionId]) return current;
      const { [correctionId]: _removed, ...remaining } = current;
      return remaining;
    });
    setSelectedId(correctionId);
    toast.success("Alteração pendente cancelada.");
  };

  const operationLabel = (operation) => {
    if (operation?.action === "link") return `Vincular a ${operation.student_name || "aluno selecionado"}`;
    if (operation?.action === "delete") return "Apagar cartão";
    if (operation?.action === "ignore") return "Manter sem aluno";
    if (operation?.action === "cpf") return "Corrigir CPF";
    return "Alteração pendente";
  };

  const submitPendingOperations = async () => {
    const operations = pendingOperationList.map((operation) => ({
      correction_id: operation.correction_id,
      action: operation.action,
      student_id: operation.student_id,
      cpf: operation.cpf,
    }));
    if (!operations.length || submittingBatch) return;
    setSubmittingBatch(true);
    try {
      const response = await api.post("/omr/corrections/student-resolution/batch", { operations });
      const payload = response.data || {};
      const appliedRows = Array.isArray(payload.applied) ? payload.applied : [];
      const failedRows = Array.isArray(payload.failed) ? payload.failed : [];
      const resultRows = Array.isArray(payload.results)
        ? payload.results
        : (Array.isArray(payload.items) ? payload.items : []);
      const appliedIds = new Set(appliedRows.map((row) => String(row?.correction_id || row?.id || "")).filter(Boolean));
      const failedIds = new Set(failedRows.map((row) => String(row?.correction_id || row?.id || "")).filter(Boolean));
      resultRows.forEach((row) => {
        const correctionId = String(row?.correction_id || row?.id || "");
        if (!correctionId) return;
        if (row?.ok === false || row?.error || ["failed", "error"].includes(String(row?.status || "").toLowerCase())) {
          failedIds.add(correctionId);
        } else if (row?.ok === true || ["applied", "success", "ok"].includes(String(row?.status || "").toLowerCase())) {
          appliedIds.add(correctionId);
        }
      });
      // A compact endpoint may return only a count after processing every
      // operation. Accept that shape only when it explicitly confirms all.
      if (!appliedIds.size && !failedIds.size && Number(payload.applied_count) === operations.length) {
        operations.forEach((operation) => appliedIds.add(operation.correction_id));
      }
      if (!appliedIds.size && !failedIds.size) {
        throw new Error("O servidor não confirmou quais alterações foram aplicadas.");
      }
      // Never silently discard an operation omitted by a partial response.
      // It remains on screen as pending until the operator retries or cancels.
      operations.forEach((operation) => {
        if (!appliedIds.has(operation.correction_id) && !failedIds.has(operation.correction_id)) {
          failedIds.add(operation.correction_id);
        }
      });
      setResolvedIds((current) => new Set([...current, ...appliedIds]));
      setPendingOperations((current) => {
        const next = { ...current };
        appliedIds.forEach((correctionId) => { delete next[correctionId]; });
        return next;
      });
      if (selected?.correction_id && appliedIds.has(selected.correction_id)) {
        setSelectedId(availableItems.find((item) => !appliedIds.has(item.correction_id))?.correction_id || "");
      }
      if (appliedIds.size) {
        toast.success(`${appliedIds.size} alteração(ões) aplicada(s).${failedIds.size ? ` ${failedIds.size} permanecem pendentes.` : ""}`);
        // Counters/reports may take longer, but the reviewer never waits to
        // continue. This is the only point that refreshes the parent list.
        Promise.resolve(onResolved?.()).catch(() => {});
      }
      if (failedIds.size) {
        const firstFailure = failedRows.find((row) => failedIds.has(String(row?.correction_id || row?.id || "")));
        toast.error(`${failedIds.size} alteração(ões) não foram enviadas${firstFailure?.message ? `: ${firstFailure.message}` : "."}`);
      }
    } catch (error) {
      toast.error("Nenhuma alteração foi perdida. Falha ao enviar lote: " + fmtError(error));
    } finally {
      setSubmittingBatch(false);
    }
  };

  const identityCurrent = Boolean(selected?.correction_id) && identityLoadedFor === selected?.correction_id;
  const identityLoading = Boolean(selected?.correction_id) && (loadingIdentity || !identityCurrent);
  const reviewContext = identityCurrent ? candidateContext : null;
  const displayedCandidates = useMemo(
    () => (identityCurrent ? candidates : []),
    [candidates, identityCurrent],
  );
  const displayedCandidateCount = identityCurrent ? candidateCount : 0;
  const visualIdentityReview = Boolean(
    reviewContext?.identity_visual_review
    || reviewContext?.tpi_name_review
    || reviewContext?.review_type === "student_identity_visual"
    || reviewContext?.review_type === "tpi_student_name"
    || reviewContext?.name_crop,
  );
  const cropSource = identityCropSrc(reviewContext?.name_crop || reviewContext?.student_name_crop);
  const showCrop = visualIdentityReview && Boolean(cropSource) && !cropUnavailable;
  const ocr = reviewContext?.ocr || selected?.student_name_ocr || {};
  const recognizedName = String(ocr.recognized_name || ocr.text || reviewContext?.recognized_name || selected?.recognized_name || "").trim();
  const rawName = String(ocr.raw_text || reviewContext?.raw_ocr_text || "").trim();
  const confidence = Number(ocr.confidence ?? reviewContext?.recognized_confidence ?? 0);
  const candidateClassOptions = useMemo(() => {
    const options = new Map();
    const scoped = Array.isArray(reviewContext?.scope?.available_classes)
      ? reviewContext.scope.available_classes
      : [];
    scoped.forEach((item) => {
      if (item?.id) options.set(String(item.id), { id: String(item.id), name: item.name || "Turma" });
    });
    displayedCandidates.forEach((student) => {
      (Array.isArray(student?.classes) ? student.classes : []).forEach((item) => {
        if (item?.id && !options.has(String(item.id))) {
          options.set(String(item.id), { id: String(item.id), name: item.name || "Turma" });
        }
      });
    });
    return [...options.values()].sort((left, right) => left.name.localeCompare(right.name, "pt-BR"));
  }, [displayedCandidates, reviewContext?.scope?.available_classes]);
  const availableCandidateClassIds = useMemo(
    () => new Set(candidateClassOptions.map((item) => item.id)),
    [candidateClassOptions],
  );
  useEffect(() => {
    setSelectedCandidateClassIds((current) => current.filter((id) => availableCandidateClassIds.has(id)));
  }, [availableCandidateClassIds]);
  const filteredCandidates = useMemo(() => {
    const query = normalizeIdentitySearch(candidateSearch);
    const cpfQuery = query.replace(/\D/g, "");
    return displayedCandidates.filter((student) => {
      const studentClasses = Array.isArray(student?.classes) ? student.classes : [];
      if (
        selectedCandidateClassIds.length
        && !studentClasses.some((item) => selectedCandidateClassIds.includes(String(item?.id || "")))
      ) return false;
      if (!query) return true;
      const name = normalizeIdentitySearch(student?.name);
      const cpf = String(student?.cpf || "").replace(/\D/g, "");
      const classes = normalizeIdentitySearch(studentClasses.map((item) => item?.name).filter(Boolean).join(" "));
      return name.includes(query)
        || classes.includes(query)
        || (cpfQuery && cpf.includes(cpfQuery));
    });
  }, [candidateSearch, displayedCandidates, selectedCandidateClassIds]);
  const completeRegisteredRoster = Boolean(reviewContext?.scope?.all_registered);
  const schoolNames = Array.isArray(reviewContext?.scope?.school_names)
    ? reviewContext.scope.school_names.filter(Boolean)
    : [];
  const schoolScopedRoster = schoolNames.length > 0;
  const hasCandidateFilter = Boolean(candidateSearch || selectedCandidateClassIds.length);
  const candidateCaption = displayedCandidateCount > displayedCandidates.length
    ? `Mostrando os primeiros ${displayedCandidates.length} de ${displayedCandidateCount} aluno(s) disponíveis.`
    : hasCandidateFilter
      ? `${filteredCandidates.length} de ${displayedCandidates.length} aluno(s) encontrado(s) neste filtro.`
      : completeRegisteredRoster
        ? `Todos os ${displayedCandidateCount || displayedCandidates.length} alunos cadastrados estão disponíveis.`
        : schoolScopedRoster
          ? `${displayedCandidateCount || displayedCandidates.length} aluno(s) da escola ${schoolNames.join(" · ")}.`
        : `${displayedCandidateCount || displayedCandidates.length} aluno(s) disponível(is) para vinculação.`;

  return (
    <section className="border-2 border-[hsl(var(--warning))] bg-card" data-testid="identity-review-workspace">
      <div className="px-6 py-5 border-b border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="h-11 w-11 shrink-0 flex items-center justify-center bg-[hsl(var(--warning))] text-black"><IdentificationCard size={24} weight="fill" /></div>
          <div>
            <div className="label-eyebrow text-[hsl(var(--warning))]">revisão de identidade</div>
            <div className="font-display text-2xl font-black tracking-tight">Cartões sem aluno vinculado</div>
            <div className="text-sm text-muted-foreground mt-1">Selecione um cartão, confira o nome e escolha o aluno sem sair desta tela.</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <span className="px-3 py-2 border border-[hsl(var(--warning))] bg-background font-mono-data font-bold text-sm">{availableItems.length} para revisar</span>
          {!!pendingOperationList.length && <span className="px-3 py-2 border border-primary bg-primary/10 text-primary font-mono-data font-bold text-sm" data-testid="inline-identity-pending-count">{pendingOperationList.length} alteração(ões) preparada(s)</span>}
          <Button
            type="button"
            className="rounded-none"
            disabled={!pendingOperationList.length || submittingBatch}
            onClick={submitPendingOperations}
            data-testid="inline-identity-submit-batch"
          >
            {submittingBatch ? <Spinner size={16} className="animate-spin" /> : <CheckCircle size={16} weight="bold" />}
            <span className="ml-2">{submittingBatch ? "Enviando…" : `Enviar alterações${pendingOperationList.length ? ` (${pendingOperationList.length})` : ""}`}</span>
          </Button>
        </div>
      </div>

      <div className="grid xl:grid-cols-[minmax(290px,0.72fr)_minmax(0,1.28fr)]">
        <aside className="border-b xl:border-b-0 xl:border-r border-border bg-muted/10 min-w-0">
          <div className="p-4 border-b border-border">
            <label className="label-eyebrow block mb-2">cartões aguardando vínculo</label>
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={cardSearch} onChange={(event) => setCardSearch(event.target.value)} placeholder="Arquivo, nome ou CPF" className="rounded-none pl-9 text-sm" />
            </div>
            <div className="text-xs font-mono-data text-muted-foreground mt-3">{visibleItems.length} de {reviewItems.length} cartão(ões) · {availableItems.length} ativos · {pendingItems.length} preparados</div>
          </div>
          <div className="max-h-[68vh] overflow-y-auto divide-y divide-border" data-testid="identity-review-card-list">
            {visibleItems.length ? visibleItems.map((item, index) => {
              const isSelected = item.correction_id === selectedId;
              const itemOperation = pendingOperations[item.correction_id];
              const itemName = item.recognized_name || item.student_name_ocr?.text || "Nome não reconhecido";
              return (
                <button
                  key={item.correction_id}
                  type="button"
                  onClick={() => chooseCard(item)}
                  className={`w-full p-4 text-left transition-colors ${isSelected ? "bg-primary text-primary-foreground" : "hover:bg-[hsl(var(--warning))]/10"}`}
                  data-testid={`identity-review-card-${item.correction_id}`}
                >
                  <div className="flex items-start gap-3">
                    <span className={`h-7 w-7 shrink-0 flex items-center justify-center border font-mono-data text-xs font-bold ${isSelected ? "border-primary-foreground/60" : "border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/10"}`}>{index + 1}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold text-sm truncate">{item.filename || "Cartão sem arquivo identificado"}</span>
                      <span className={`block text-xs mt-1 truncate ${isSelected ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{itemName}</span>
                      <span className={`block text-[10px] font-mono-data mt-1 ${isSelected ? "text-primary-foreground/75" : "text-muted-foreground"}`}>CPF {item.cpf || "não informado"}</span>
                      {itemOperation && <span className={`block text-[10px] font-mono-data font-bold uppercase tracking-widest mt-2 ${isSelected ? "text-primary-foreground" : "text-primary"}`}>Preparado: {operationLabel(itemOperation)}</span>}
                    </span>
                  </div>
                </button>
              );
            }) : <div className="p-6 text-sm text-muted-foreground">Nenhum cartão encontrado neste filtro.</div>}
          </div>
        </aside>

        <div className="min-w-0 p-5 lg:p-6" data-testid="identity-review-selected-pane">
          {!selected ? (
            <div className="min-h-72 border border-dashed border-border flex flex-col items-center justify-center text-center p-8">
              <IdentificationCard size={38} weight="duotone" className="text-primary mb-4" />
              <div className="font-display text-2xl font-black tracking-tight">Selecione um cartão</div>
              <div className="text-sm text-muted-foreground mt-2 max-w-md">A foto do nome e a lista de alunos serão carregadas somente para o cartão escolhido.</div>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="label-eyebrow">cartão selecionado</div>
                  <div className="font-display text-xl font-black tracking-tight break-all mt-1">{selected.filename || "Cartão sem arquivo identificado"}</div>
                  <div className="text-xs font-mono-data text-muted-foreground mt-2">CPF {selected.cpf || "não informado"}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2 shrink-0">
                  <span className={`px-2 py-1 border text-[10px] font-mono-data font-bold uppercase tracking-widest ${selectedIsPending ? "border-primary bg-primary/10 text-primary" : "border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/10"}`}>{selectedIsPending ? "Alteração preparada" : "Sem aluno vinculado"}</span>
                  {selectedIsPending ? (
                    <>
                      <span className="text-xs font-mono-data text-primary">{operationLabel(selectedOperation)}</span>
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-none"
                        disabled={submittingBatch}
                        onClick={() => cancelPendingOperation()}
                        data-testid="inline-identity-cancel-pending"
                      >
                        <XCircle size={16} /><span className="ml-2">Cancelar alteração</span>
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-none"
                        disabled={submittingBatch}
                        onClick={ignoreSelectedCard}
                        data-testid="inline-identity-ignore-card"
                      >
                        <XCircle size={16} /><span className="ml-2">Manter sem aluno</span>
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-none border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                        disabled={submittingBatch}
                        onClick={deleteSelectedCard}
                        data-testid="inline-identity-delete-card"
                      >
                        <Trash size={16} /><span className="ml-2">Apagar cartão</span>
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {selectedIsPending && (
                <div className="border border-primary bg-primary/5 px-4 py-3 text-sm text-primary" data-testid="inline-identity-pending-notice">
                  Esta decisão ainda está apenas preparada nesta tela. Ela pode ser cancelada e só será registrada no banco quando você clicar em <strong>Enviar alterações</strong>.
                </div>
              )}

              <div className={visualIdentityReview ? "grid gap-4 lg:grid-cols-[minmax(330px,0.95fr)_minmax(0,1.05fr)]" : ""}>
                {visualIdentityReview && (
                  <figure className="border-2 border-primary/40 bg-primary/5 p-4" data-testid="inline-tpi-student-name-crop" data-review-kind="student-identity-crop">
                    <figcaption className="label-eyebrow text-primary mb-3">foto do nome no gabarito</figcaption>
                    {identityLoading && !reviewContext ? (
                      <div className="min-h-44 flex flex-col items-center justify-center gap-3 text-sm text-muted-foreground"><Spinner size={24} className="animate-spin text-primary" />Preparando a foto do nome…</div>
                    ) : showCrop ? (
                      <img src={cropSource} alt="Foto do campo de nome no gabarito do aluno" className="w-full min-h-40 max-h-80 object-contain bg-white border border-border" onError={() => setCropUnavailable(true)} />
                    ) : (
                      <div className="min-h-44 flex items-center text-sm text-muted-foreground leading-relaxed">Não foi possível recuperar o recorte deste cartão. A seleção manual permanece disponível.</div>
                    )}
                  </figure>
                )}
                <div className="border border-border bg-muted/20 p-4 text-sm space-y-2">
                  <div className="label-eyebrow">leitura do cartão</div>
                  {recognizedName ? <div><span className="text-muted-foreground">Nome sugerido:</span> <strong>{recognizedName}</strong></div>
                    : rawName ? <div><span className="text-muted-foreground">Leitura parcial:</span> <strong>{rawName}</strong></div>
                      : <div><span className="text-muted-foreground">Nome lido:</span> não disponível</div>}
                  <div className="text-xs font-mono-data text-muted-foreground">OCR {ocr.status || reviewContext?.ocr_status || "pendente"}{confidence > 0 ? ` · confiança ${confidence.toFixed(1)}%` : ""}</div>
                  <div className="pt-2 text-xs text-muted-foreground leading-relaxed">A escolha é manual e auditável. A correção, a nota e as respostas do cartão não serão alteradas.</div>
                </div>
              </div>

              <div>
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2 mb-2">
                  <div><div className="label-eyebrow">alunos disponíveis</div><div className="text-sm text-muted-foreground mt-1">{schoolScopedRoster ? `Lista limitada automaticamente à escola ${schoolNames.join(" · ")}.` : completeRegisteredRoster ? "Cartão histórico sem escola definida: lista geral de recuperação." : "Filtre por nome ou CPF, ou selecione diretamente da lista."}</div></div>
                  <div className="text-xs font-mono-data text-muted-foreground">{identityLoading ? "Carregando…" : candidateCaption}</div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    value={candidateSearch}
                    onChange={(event) => setCandidateSearch(event.target.value)}
                    placeholder="Filtrar alunos por nome ou CPF"
                    className="rounded-none"
                    data-testid="inline-identity-student-search"
                  />
                  <Button type="button" variant="outline" className="rounded-none shrink-0" disabled={identityLoading || submittingBatch} onClick={() => loadIdentity(selected.correction_id, { force: true })} data-testid="inline-identity-student-refresh" title="Atualizar recorte e lista no servidor">
                    {identityLoading ? <Spinner size={16} className="animate-spin" /> : <ArrowClockwise size={16} />}<span className="ml-2">Atualizar</span>
                  </Button>
                  {candidateSearch && <Button type="button" variant="outline" className="rounded-none shrink-0" disabled={identityLoading} onClick={() => setCandidateSearch("")}>Limpar filtro</Button>}
                </div>
                {candidateClassOptions.length > 0 && (
                  <div className="mt-3 border border-border bg-muted/10 p-3" data-testid="inline-identity-class-filter">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div><div className="label-eyebrow">filtrar por turma(s)</div><div className="text-xs text-muted-foreground mt-1">Você pode combinar várias turmas. Sem seleção, todos os alunos desta escola aparecem.</div></div>
                      {selectedCandidateClassIds.length > 0 && (
                        <button type="button" className="text-xs font-semibold text-primary hover:underline" onClick={() => setSelectedCandidateClassIds([])}>Mostrar todas</button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {candidateClassOptions.map((classItem) => {
                        const selectedClass = selectedCandidateClassIds.includes(classItem.id);
                        return (
                          <button
                            key={classItem.id}
                            type="button"
                            aria-pressed={selectedClass}
                            onClick={() => setSelectedCandidateClassIds((current) => (
                              current.includes(classItem.id)
                                ? current.filter((id) => id !== classItem.id)
                                : [...current, classItem.id]
                            ))}
                            className={`border px-3 py-2 text-xs font-mono-data transition-colors ${selectedClass ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:border-primary"}`}
                            data-testid={`inline-identity-class-${classItem.id}`}
                          >
                            {classItem.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="mt-3 border border-border divide-y divide-border max-h-[42vh] overflow-y-auto" data-testid="inline-identity-student-candidates">
                  {identityLoading ? <div className="p-6 text-sm text-muted-foreground">Carregando foto do nome e alunos disponíveis…</div>
                    : filteredCandidates.length ? filteredCandidates.map((student) => {
                      const similarity = candidateSimilarityPercent(student);
                      const classes = Array.isArray(student.classes) ? student.classes : [];
                      return (
                        <button key={student.id} type="button" disabled={Boolean(selectedIsPending || submittingBatch)} onClick={() => linkCandidate(student)} className="w-full p-4 text-left hover:bg-primary/5 transition-colors disabled:opacity-50" data-testid={`inline-identity-student-${student.id}`}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0"><div className="font-semibold break-words">{student.name || "Aluno sem nome"}</div><div className="text-xs text-muted-foreground font-mono-data mt-1">CPF {student.cpf || "não informado"}</div>{classes.length > 0 && <div className="text-xs text-muted-foreground mt-1">{classes.map((item) => item.name).filter(Boolean).join(" · ")}</div>}</div>
                            <span className="shrink-0 text-xs font-mono-data text-primary">{selectedIsPending ? "Preparado" : "Vincular"}</span>
                          </div>
                          {similarity !== null && <div className="mt-2 text-[10px] font-mono-data text-muted-foreground">semelhança OCR: {similarity.toFixed(0)}%</div>}
                        </button>
                      );
                    }) : <div className="p-6 text-sm text-muted-foreground">Nenhum aluno encontrado neste filtro.</div>}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function ResultsPanel({ data, loading, onReload, canWrite = true, onReviewIdentity }) {
  const [search, setSearch] = useState("");
  const unlinkedRows = useMemo(
    () => (data?.results || []).filter((row) => !row.student_id),
    [data?.results],
  );
  const rows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("pt-BR");
    if (!term) return data?.results || [];
    return (data?.results || []).filter((row) => (
      (row.student_name || "").toLocaleLowerCase("pt-BR").includes(term)
      || (row.cpf || "").includes(term.replace(/\D/g, ""))
      || (row.filename || "").toLocaleLowerCase("pt-BR").includes(term)
    ));
  }, [data, search]);

  const remove = async (id) => {
    if (!window.confirm("Remover este cartão e sua correção?")) return;
    try {
      await api.delete(`/omr/corrections/${id}`);
      toast.success("Cartão removido");
      await onReload();
    } catch (error) {
      toast.error(fmtError(error));
    }
  };

  if (loading) return <div className="p-16 border border-border text-center label-eyebrow">calculando resultados…</div>;

  return (
    <div className="space-y-4">
      <div className="relative max-w-lg">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar por aluno, CPF ou arquivo…"
          className="rounded-none pl-10 font-mono-data"
        />
      </div>

      {data?.summary?.tri_available ? (
        <div className="border border-border bg-muted/20 p-4 text-sm text-muted-foreground">
          A nota TRI usa Rasch/1PL e a escala do Enamed (0–100). Nota igual ou superior a 60,0 é classificada como proficiente.
        </div>
      ) : (
        <div className="border border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/5 p-4 text-sm text-muted-foreground">
          A nota por acertos está disponível. A nota TRI exige pelo menos 20 cartões válidos e cinco questões válidas.
        </div>
      )}

      {!!unlinkedRows.length && (
        <section className="border-2 border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/5" data-testid="results-unlinked-students">
          <div className="px-5 py-4 border-b border-[hsl(var(--warning))]/40 flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 shrink-0 flex items-center justify-center bg-[hsl(var(--warning))] text-black"><Warning size={21} weight="fill" /></div>
              <div>
                <div className="font-display text-xl font-black tracking-tight">{unlinkedRows.length} cartão(ões) sem aluno vinculado</div>
                <div className="text-sm text-muted-foreground">Use a aba de Revisão para conferir o nome e escolher o aluno correto sem sair da prova.</div>
              </div>
            </div>
            <span className="label-eyebrow text-[hsl(var(--warning))]">revisão necessária</span>
          </div>
          <div className="divide-y divide-[hsl(var(--warning))]/20 max-h-72 overflow-y-auto">
            {unlinkedRows.map((row) => (
              <div key={row.correction_id} className="px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{row.filename || "Cartão sem arquivo identificado"}</div>
                  <div className="text-xs text-muted-foreground font-mono-data mt-1">CPF {row.cpf || "não informado"}</div>
                </div>
                <Button
                  type="button"
                  onClick={() => onReviewIdentity?.(row.correction_id)}
                  disabled={!canWrite || !onReviewIdentity}
                  className="rounded-none shrink-0"
                  data-testid={`result-review-unlinked-${row.correction_id}`}
                >
                  Revisar nome
                </Button>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="border border-border bg-card overflow-x-auto">
        <div className="min-w-[1240px] grid grid-cols-[70px_1fr_150px_100px_90px_90px_110px_110px_140px_110px] gap-4 px-5 py-3 bg-muted/30 border-b border-border label-eyebrow">
          <div>#</div><div>Aluno · arquivo</div><div>CPF</div><div>Acertos</div><div>Erros</div><div>Vazias</div><div>Nota acertos</div><div>Nota TRI</div><div>Classificação</div><div></div>
        </div>
        {rows.length === 0 ? (
          <div className="p-16 text-center label-eyebrow">nenhum cartão corrigido nesta prova</div>
        ) : rows.map((row) => (
          <div key={row.correction_id} className={`min-w-[1240px] grid grid-cols-[70px_1fr_150px_100px_90px_90px_110px_110px_140px_110px] gap-4 px-5 py-4 border-b border-border last:border-b-0 items-center hover:bg-muted/20 ${!row.student_id ? "bg-[hsl(var(--warning))]/5" : ""}`}>
            <div className="font-display text-xl font-black">{row.rank}º</div>
            <div className="min-w-0">
              <div className="font-semibold text-sm truncate">{row.student_name || "Aluno não identificado"}</div>
              {!row.student_id && <div className="mt-1 text-[10px] font-mono-data font-bold text-[hsl(var(--warning))] uppercase tracking-widest">Sem aluno vinculado</div>}
              <div className="text-[10px] text-muted-foreground font-mono-data truncate">{row.filename}</div>
            </div>
            <div className="font-mono-data text-sm">{row.cpf || "—"}</div>
            <div className="font-mono-data font-bold">{row.hits}<span className="text-xs text-muted-foreground">/{row.total_valid}</span></div>
            <div className="font-mono-data text-destructive">{row.misses}</div>
            <div className="font-mono-data text-muted-foreground">{row.blank_valid}</div>
            <div className={`font-mono-data font-black ${scoreTone(row.score)}`}>{row.score.toFixed(1)}%</div>
            <div className="font-mono-data font-black text-primary">{row.tri_score == null ? "—" : Number(row.tri_score).toFixed(1)}</div>
            <div className={`text-xs font-semibold ${row.proficient === true ? "text-[hsl(var(--success))]" : row.proficient === false ? "text-destructive" : "text-muted-foreground"}`}>
              {row.proficiency_status || "TRI indisponível"}
            </div>
            <div className="flex justify-end items-center gap-3">
              {row.student_id ? (
                <Link to={`/correction/${row.correction_id}`} className="text-xs font-mono-data uppercase tracking-widest text-primary hover:underline">Ver cartão</Link>
              ) : (
                <button type="button" onClick={() => onReviewIdentity?.(row.correction_id)} disabled={!canWrite || !onReviewIdentity} className="text-xs font-mono-data uppercase tracking-widest text-primary hover:underline disabled:opacity-50">Revisar nome</button>
              )}
              {canWrite && <button onClick={() => remove(row.correction_id)} className="text-muted-foreground hover:text-destructive" title="Remover cartão">
                <Trash size={14} weight="bold" />
              </button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AnalyticsPanel({ data, loading }) {
  const summary = data?.summary || {};
  const scoreDistribution = data?.score_distribution || [];
  const questionAnalysis = useMemo(() => data?.question_analysis || [], [data?.question_analysis]);
  const classComparison = data?.class_comparison || [];

  const hardestQuestions = useMemo(() => (
    [...questionAnalysis]
      .sort((a, b) => (
        a.correct_percentage - b.correct_percentage
        || b.blank_percentage - a.blank_percentage
        || a.question - b.question
      ))
      .slice(0, 15)
  ), [questionAnalysis]);

  if (loading) {
    return <div className="p-16 border border-border text-center label-eyebrow">calculando gráficos…</div>;
  }
  if (!summary.cards) {
    return <div className="p-16 border border-border text-center label-eyebrow">nenhum cartão corrigido para gerar gráficos</div>;
  }

  const questionChartWidth = Math.max(980, questionAnalysis.length * 42);
  const classChartWidth = Math.max(620, classComparison.length * 150);

  return (
    <div className="space-y-6" data-testid="exam-score-charts">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatBox label="mediana" value={`${Number(summary.median_score || 0).toFixed(1)}%`} hint="nota central da turma" />
        <StatBox label="desvio padrão" value={Number(summary.standard_deviation || 0).toFixed(1)} hint="dispersão das notas" />
        <StatBox label="maior nota" value={`${Number(summary.highest_score || 0).toFixed(1)}%`} hint="melhor desempenho" />
        <StatBox label="menor nota" value={`${Number(summary.lowest_score || 0).toFixed(1)}%`} hint="menor desempenho" />
      </div>

      <AcademicDomainChart
        domains={data?.academic_domains}
        eyebrow="mapa curricular da prova"
        title="Acertos e erros por disciplina e conteúdo"
        description="Compare as áreas de maior domínio e os pontos de atenção dos alunos que realizaram esta prova. Clique em uma área para detalhar seus contextos."
      />

      <section className="border border-border bg-card p-5">
        <div className="mb-5">
          <div className="label-eyebrow">distribuição das notas</div>
          <h2 className="font-display text-2xl font-black tracking-tight mt-1">Quantidade de alunos por faixa percentual</h2>
        </div>
        <div className="h-[320px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={scoreDistribution} margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value, name, item) => [
                  `${value} aluno(s) · ${Number(item?.payload?.percentage || 0).toFixed(1)}%`,
                  "Cartões",
                ]}
                labelFormatter={(label) => `Nota ${label}%`}
              />
              <Bar dataKey="count" name="Cartões" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="border border-border bg-card p-5">
        <div className="mb-4">
          <div className="label-eyebrow">desempenho questão a questão</div>
          <h2 className="font-display text-2xl font-black tracking-tight mt-1">Percentual de acertos, erros e respostas vazias</h2>
          <p className="text-sm text-muted-foreground mt-2">
            Questões anuladas ou sem gabarito não aparecem. Role horizontalmente para visualizar toda a prova.
          </p>
        </div>
        <div className="overflow-x-auto border border-border/70">
          <div style={{ width: questionChartWidth, height: 390 }} className="p-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={questionAnalysis} margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" interval={0} tick={{ fontSize: 10 }} />
                <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(value, name) => [`${Number(value).toFixed(1)}%`, name]}
                  labelFormatter={(label, payload) => {
                    const item = payload?.[0]?.payload;
                    return `${label} · gabarito ${item?.answer_key || "—"} · ${item?.difficulty || ""}`;
                  }}
                />
                <Legend />
                <Bar dataKey="correct_percentage" name="Acertos" stackId="score" fill="hsl(var(--success))" />
                <Bar dataKey="miss_percentage" name="Erros" stackId="score" fill="hsl(var(--destructive))" />
                <Bar dataKey="blank_percentage" name="Vazias" stackId="score" fill="hsl(var(--warning))" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {classComparison.length > 1 && (
        <section className="border border-border bg-card p-5">
          <div className="mb-5">
            <div className="label-eyebrow">comparação entre turmas</div>
            <h2 className="font-display text-2xl font-black tracking-tight mt-1">Média e mediana por turma</h2>
          </div>
          <div className="overflow-x-auto">
            <div style={{ width: classChartWidth, height: 340 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={classComparison} margin={{ top: 10, right: 16, left: 0, bottom: 36 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="class_name" interval={0} angle={-18} textAnchor="end" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(value, name) => [`${Number(value).toFixed(1)}%`, name]}
                    labelFormatter={(label, payload) => `${label} · ${payload?.[0]?.payload?.cards || 0} cartão(ões)`}
                  />
                  <Legend />
                  <Bar dataKey="average_score" name="Média" fill="hsl(var(--primary))" />
                  <Bar dataKey="median_score" name="Mediana" fill="hsl(var(--muted-foreground))" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>
      )}

      <section className="border border-border bg-card overflow-x-auto">
        <div className="px-5 py-4 border-b border-border">
          <div className="label-eyebrow">prioridade pedagógica</div>
          <h2 className="font-display text-2xl font-black tracking-tight mt-1">15 questões com menor percentual de acerto</h2>
        </div>
        <div className="min-w-[820px] grid grid-cols-[90px_110px_130px_130px_130px_1fr] gap-4 px-5 py-3 bg-muted/30 border-b border-border label-eyebrow">
          <div>Questão</div><div>Gabarito</div><div>Acertos</div><div>Erros</div><div>Vazias</div><div>Classificação</div>
        </div>
        {hardestQuestions.map((item) => (
          <div key={item.question} className="min-w-[820px] grid grid-cols-[90px_110px_130px_130px_130px_1fr] gap-4 px-5 py-4 border-b border-border last:border-b-0 items-center">
            <div className="font-display text-xl font-black">Q{item.question}</div>
            <div className="font-mono-data font-bold">{item.answer_key}</div>
            <div className="font-mono-data text-[hsl(var(--success))]">{item.hits} · {Number(item.correct_percentage).toFixed(1)}%</div>
            <div className="font-mono-data text-destructive">{item.misses} · {Number(item.miss_percentage).toFixed(1)}%</div>
            <div className="font-mono-data text-[hsl(var(--warning))]">{item.blank} · {Number(item.blank_percentage).toFixed(1)}%</div>
            <div className="text-sm font-semibold">{item.difficulty}</div>
          </div>
        ))}
      </section>
    </div>
  );
}

function PerceptionPanel({ data, loading }) {
  if (loading) return <div className="p-16 border border-border text-center label-eyebrow">calculando distribuição…</div>;
  const distribution = data?.perception_distribution || [];
  if (!distribution.length || !data?.summary?.cards) {
    return <div className="p-16 border border-border text-center label-eyebrow">nenhuma resposta de percepção registrada</div>;
  }

  return (
    <div className="space-y-4">
      <div className="border border-border bg-card p-5 text-sm text-muted-foreground">
        Cada pergunta possui seu próprio gráfico de barras. Os percentuais usam o total de cartões corrigidos nesta prova como denominador; “Sem resposta” inclui marcações vazias ou indeterminadas.
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      {distribution.map((item) => {
        const perceptionOptions = item.options?.length ? item.options : ["A", "B", "C", "D", "E"];
        const chartData = [...perceptionOptions, "blank"].map((option) => ({
          option: option === "blank" ? "Sem resposta" : option,
          fullLabel: option === "blank" ? "Sem resposta" : `${option} · ${item.option_labels?.[option] || option}`,
          count: Number(item.counts?.[option] || 0),
          percentage: Number(item.percentages?.[option] || 0),
        }));
        return (
        <div key={item.question} className="border border-border bg-card p-5 min-w-0">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <div className="label-eyebrow">{item.question}</div>
              <h3 className="font-display text-xl font-black leading-tight mt-1">{item.prompt || `Pergunta de percepção ${item.position || ""}`}</h3>
              <div className="text-xs text-muted-foreground font-mono-data mt-2">{item.answered} respondida(s) · {item.total_cards} cartão(ões)</div>
            </div>
          </div>
          <div className="h-[285px] w-full" role="img" aria-label={`Distribuição das respostas de ${item.question}`}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ top: 8, right: 28, bottom: 8, left: 16 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="option" width={92} tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(value, _name, context) => [
                    `${value} aluno(s) · ${Number(context?.payload?.percentage || 0).toFixed(1)}%`,
                    context?.payload?.fullLabel || "Respostas",
                  ]}
                />
                <Bar dataKey="count" name="Alunos" fill="hsl(var(--primary))" maxBarSize={30} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 border-t border-border pt-3">
            {perceptionOptions.map((option) => (
              <div key={option} className="text-xs text-muted-foreground truncate" title={item.option_labels?.[option] || option}>
                <b className="text-foreground font-mono-data">{option}</b> · {item.option_labels?.[option] || option}
              </div>
            ))}
          </div>
        </div>
        );
      })}
      </div>
    </div>
  );
}

function ReviewActions({ exam, onChanged }) {
  const [reprocessing, setReprocessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [deleting, setDeleting] = useState(false);

  const reprocessExam = async () => {
    if (!window.confirm(`Reprocessar todos os cartões da prova “${exam.name}”? As leituras atuais serão substituídas.`)) return;
    setReprocessing(true);
    setProgress(0);
    try {
      const queued = await api.post("/omr/corrections/reprocess-all", {
        exam_id: exam.id,
        fill_threshold: 0.22,
        dominance: 1.25,
      });
      const { job_id: jobId, total } = queued.data;
      toast.success(`${total} cartão(ões) desta prova colocado(s) na fila`);
      while (true) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const response = await api.get(`/omr/jobs/${jobId}`);
        const job = response.data;
        const denominator = Math.max(Number(job.total_pages || total || 1), 1);
        setProgress(Math.min(100, Math.round((Number(job.processed_pages || 0) / denominator) * 100)));
        if (job.status === "completed") {
          const failures = Number(job.failure_count || 0);
          toast.success(`${Number(job.success_count || 0)} cartão(ões) reprocessado(s)${failures ? ` · ${failures} falharam` : ""}`);
          await onChanged?.();
          break;
        }
        if (job.status === "failed") throw new Error(job.error || "Reprocessamento falhou");
      }
    } catch (error) {
      toast.error("Falha ao reprocessar esta prova: " + fmtError(error));
    } finally {
      setReprocessing(false);
    }
  };

  const deleteCorrections = async () => {
    if (!window.confirm(`Apagar definitivamente todas as correções da prova “${exam.name}”? A prova e o gabarito serão preservados.`)) return;
    setDeleting(true);
    try {
      const response = await api.delete(`/exams/${exam.id}/corrections`);
      toast.success(`${Number(response.data.deleted || 0)} correção(ões) apagada(s). A prova foi preservada.`);
      await onChanged?.();
    } catch (error) {
      toast.error("Falha ao apagar as correções: " + fmtError(error));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="border border-border bg-card p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4" data-testid="exam-correction-actions">
      <div>
        <div className="font-display text-xl font-black tracking-tight">Ações dos cartões desta prova</div>
        <div className="text-sm text-muted-foreground mt-1">Releia todos com o OMR atual ou remova somente as correções, preservando prova e gabarito.</div>
      </div>
      <div className="flex flex-wrap gap-2 shrink-0">
        <Button
          variant="outline"
          onClick={reprocessExam}
          disabled={reprocessing || deleting}
          data-testid="exam-reprocess-all-btn"
          className="rounded-none border-primary text-primary font-mono-data uppercase tracking-widest text-xs"
        >
          <ArrowClockwise size={14} weight="bold" className={`mr-2 ${reprocessing ? "animate-spin" : ""}`} />
          {reprocessing ? `Reprocessando ${progress}%` : "Reprocessar cartões"}
        </Button>
        <Button
          variant="outline"
          onClick={deleteCorrections}
          disabled={reprocessing || deleting}
          data-testid="exam-delete-corrections-btn"
          className="rounded-none border-destructive text-destructive font-mono-data uppercase tracking-widest text-xs"
        >
          <Trash size={14} weight="bold" className="mr-2" />
          {deleting ? "Apagando…" : "Apagar correções"}
        </Button>
      </div>
    </div>
  );
}


function ReviewPanel({ exam, refreshKey = 0, onCountChange, onResultsReload, selectedIdentityCorrectionId = "" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolvingDuplicate, setResolvingDuplicate] = useState("");
  const [selectedReviewIds, setSelectedReviewIds] = useState([]);
  const [skippingReviews, setSkippingReviews] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/exams/${exam.id}/review-queue`);
      setData(response.data);
      const pendingIds = new Set((response.data?.manual_reviews || []).map((item) => item.correction_id));
      setSelectedReviewIds((current) => current.filter((id) => pendingIds.has(id)));
      const counts = response.data?.counts || {};
      onCountChange?.(
        Number(counts.manual_reviews || 0)
        + Number(counts.duplicates || 0)
        + Number(counts.class_issues || 0)
        + Number(counts.student_issues || 0),
      );
    } catch (error) {
      toast.error("Falha ao carregar a fila de revisão: " + fmtError(error));
    } finally {
      setLoading(false);
    }
  }, [exam.id, onCountChange]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const resolveDuplicate = async (correctionId, action) => {
    setResolvingDuplicate(correctionId);
    try {
      await api.post(`/omr/duplicates/${correctionId}/resolve`, { action });
      toast.success(
        action === "keep_existing" ? "Correção antiga mantida"
          : action === "keep_new" ? "Nova correção mantida"
            : "As duas correções foram mantidas",
      );
      await Promise.all([load(), onResultsReload?.()]);
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setResolvingDuplicate("");
    }
  };

  const toggleReviewSelection = (correctionId) => {
    setSelectedReviewIds((current) => current.includes(correctionId)
      ? current.filter((id) => id !== correctionId)
      : [...current, correctionId]);
  };

  const skipSelectedReviews = async () => {
    if (!selectedReviewIds.length) {
      toast.error("Selecione ao menos um cartão");
      return;
    }
    if (!window.confirm(`Marcar ${selectedReviewIds.length} cartão(ões) como “não revisar”? As respostas OMR serão preservadas.`)) return;
    setSkippingReviews(true);
    try {
      const response = await api.post(`/exams/${exam.id}/reviews/bulk`, {
        action: "skip",
        correction_ids: selectedReviewIds,
      });
      toast.success(`${response.data?.updated || selectedReviewIds.length} cartão(ões) removido(s) da fila de revisão`);
      setSelectedReviewIds([]);
      await Promise.all([load(), onResultsReload?.()]);
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setSkippingReviews(false);
    }
  };

  if (loading) {
    return <div className="border border-border bg-card p-8 label-eyebrow">carregando fila de revisão…</div>;
  }

  const manual = data?.manual_reviews || [];
  const duplicates = data?.duplicates || [];
  const classIssues = data?.class_issues || [];
  const studentIssues = data?.student_issues || [];
  const total = manual.length + duplicates.length + classIssues.length + studentIssues.length;

  if (!total) {
    return (
      <div className="border border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/5 p-8 flex items-start gap-4">
        <CheckCircle size={28} weight="fill" className="text-[hsl(var(--success))] shrink-0" />
        <div>
          <div className="font-display text-2xl font-black tracking-tight">Nenhum cartão aguardando revisão.</div>
          <div className="text-sm text-muted-foreground mt-1">Todas as células deste lote já foram reconhecidas ou confirmadas pelo operador.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="exam-review-queue">
      <div className="border border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/5 p-5 flex items-start gap-4">
        <Warning size={24} weight="fill" className="text-[hsl(var(--warning))] shrink-0" />
        <div>
          <div className="font-display text-2xl font-black tracking-tight">{total} ocorrência(s) aguardando decisão</div>
          <div className="text-sm text-muted-foreground mt-1">
            Os cartões com mais respostas objetivas vazias aparecem primeiro. Use as áreas de revisão abaixo para conferir a imagem e confirmar as decisões necessárias.
          </div>
        </div>
      </div>

      {!!studentIssues.length && (
        <StudentIdentityReviewWorkspace
          items={studentIssues}
          selectedCorrectionId={selectedIdentityCorrectionId}
          onResolved={async () => { await Promise.all([load(), onResultsReload?.()]); }}
        />
      )}

      {!!manual.length && (
        <section className="border border-border bg-card">
          <div className="px-5 py-3 border-b border-border flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <div>
              <div className="font-mono-data font-bold">Cartões para revisão manual</div>
              <span className="label-eyebrow">{manual.length} pendente(s) · {selectedReviewIds.length} selecionado(s)</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                className="rounded-none"
                onClick={() => setSelectedReviewIds(manual.map((item) => item.correction_id))}
                disabled={skippingReviews || selectedReviewIds.length === manual.length}
              >Selecionar todos</Button>
              {!!selectedReviewIds.length && (
                <Button variant="outline" className="rounded-none" onClick={() => setSelectedReviewIds([])} disabled={skippingReviews}>Limpar</Button>
              )}
              <Button
                variant="outline"
                className="rounded-none border-[hsl(var(--warning))] text-foreground"
                onClick={skipSelectedReviews}
                disabled={!selectedReviewIds.length || skippingReviews}
                data-testid="skip-selected-reviews"
              >{skippingReviews ? "Processando…" : `Não revisar (${selectedReviewIds.length})`}</Button>
            </div>
          </div>
          <div className="divide-y divide-border">
            {manual.map((item, index) => (
              <div key={item.correction_id} className="p-5 flex flex-col lg:flex-row lg:items-center gap-4">
                <label className="flex items-center justify-center shrink-0 cursor-pointer" title="Selecionar cartão">
                  <input
                    type="checkbox"
                    className="w-4 h-4"
                    checked={selectedReviewIds.includes(item.correction_id)}
                    onChange={() => toggleReviewSelection(item.correction_id)}
                    aria-label={`Selecionar ${item.filename}`}
                  />
                </label>
                <div className="w-9 h-9 border border-border flex items-center justify-center font-display font-black shrink-0">
                  {index + 1}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold break-all">{item.filename}</div>
                  <div className="text-xs text-muted-foreground font-mono-data mt-1">
                    CPF {item.cpf || "não reconhecido"}
                    {item.rotation_degrees === 180 ? " · leitura escolhida a 180°" : ""}
                  </div>
                  {!!item.objective_blank_count && (
                    <div className="text-sm mt-2">
                      <strong>{item.objective_blank_count}</strong> questão(ões) objetiva(s) vazia(s): {item.objective_blank_questions.join(", ")}
                    </div>
                  )}
                  {!!item.perception_blank_count && (
                    <div className="text-sm mt-1">
                      <strong>{item.perception_blank_count}</strong> campo(s) de percepção vazio(s): {item.perception_blank_fields.join(", ")}
                    </div>
                  )}
                </div>
                <Link
                  to={`/correction/${item.correction_id}?review=1`}
                  className="px-4 py-2 border border-primary text-primary hover:bg-primary hover:text-primary-foreground text-xs font-mono-data uppercase tracking-widest text-center shrink-0"
                >
                  Revisar cartão
                </Link>
              </div>
            ))}
          </div>
        </section>
      )}

      {!!duplicates.length && (
        <section className="border border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/5">
          <div className="px-5 py-3 border-b border-border font-mono-data font-bold">Cartões escaneados mais de uma vez</div>
          <div className="divide-y divide-border">
            {duplicates.map((item) => (
              <div key={item.correction_id} className="p-5">
                <div className="font-semibold">{item.filename}</div>
                <div className="text-sm mt-2">
                  {item.identical
                    ? "A nova leitura é igual à correção anterior."
                    : `Existem ${item.objective_difference_count || 0} diferença(s) objetiva(s) e ${item.perception_difference_count || 0} na percepção.`}
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button variant="outline" className="rounded-none" disabled={resolvingDuplicate === item.correction_id}
                    onClick={() => resolveDuplicate(item.correction_id, "keep_existing")}>Manter antiga</Button>
                  <Button variant="outline" className="rounded-none" disabled={resolvingDuplicate === item.correction_id}
                    onClick={() => resolveDuplicate(item.correction_id, "keep_new")}>Manter nova</Button>
                  <Button className="rounded-none" disabled={resolvingDuplicate === item.correction_id}
                    onClick={() => resolveDuplicate(item.correction_id, "keep_both")}>Manter ambas</Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {!!classIssues.length && (
        <section className="border border-border bg-card">
          <div className="px-5 py-3 border-b border-border font-mono-data font-bold">Turma não identificada</div>
          {classIssues.map((item) => (
            <div key={item.correction_id} className="p-5 border-b border-border last:border-b-0">
              <div className="font-semibold">{item.filename}</div>
              <div className="text-sm text-muted-foreground mt-1">{item.message}</div>
            </div>
          ))}
        </section>
      )}

    </div>
  );
}


export default function ExamWorkspace({ exam, classNames = [], classOptions = [], semesterName, onBack, onEdit, onExamChanged, initialTab = "upload" }) {
  const accessRank = { exam: 1, global: 2, individual: 3 };
  const accessLevel = exam.user_access_level || "individual";
  const canWrite = exam.can_write !== false;
  const canSeeGlobalResults = (accessRank[accessLevel] || 0) >= accessRank.global;
  const canSeeIndividualResults = (accessRank[accessLevel] || 0) >= accessRank.individual;
  const [activeTab, setActiveTab] = useState(canWrite ? initialTab : canSeeGlobalResults ? "analytics" : "overview");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewCount, setReviewCount] = useState(0);
  const [reviewRefreshKey, setReviewRefreshKey] = useState(0);
  const [identityReviewTargetId, setIdentityReviewTargetId] = useState("");

  useEffect(() => {
    setActiveTab(canWrite ? initialTab : canSeeGlobalResults ? "analytics" : "overview");
    setIdentityReviewTargetId("");
  }, [exam.id, initialTab, canWrite, canSeeGlobalResults]);

  const openIdentityReview = useCallback((correctionId) => {
    if (!canWrite || !correctionId) return;
    setIdentityReviewTargetId(correctionId);
    setReviewRefreshKey((value) => value + 1);
    setActiveTab("review");
  }, [canWrite]);

  const loadResults = useCallback(async () => {
    if (!canSeeGlobalResults) { setData(null); setLoading(false); return; }
    setLoading(true);
    try {
      const response = await api.get(`/exams/${exam.id}/results`);
      setData(response.data);
    } catch (error) {
      toast.error("Falha ao carregar resultados: " + fmtError(error));
    } finally {
      setLoading(false);
    }
  }, [exam.id, canSeeGlobalResults]);

  useEffect(() => { loadResults(); }, [loadResults]);

  const afterUpload = useCallback(async () => {
    await loadResults();
    setReviewRefreshKey((value) => value + 1);
    setActiveTab("review");
  }, [loadResults]);

  const exportXlsx = async () => {
    try {
      const response = await api.get(`/exams/${exam.id}/results.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `resultados_${exam.name.replace(/[^a-z0-9]+/gi, "_")}.xlsx`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Falha ao exportar: " + fmtError(error));
    }
  };

  const summary = data?.summary || {};
  const totalValid = data?.exam?.total_valid ?? Object.values(exam.answers || {}).filter(Boolean).length;

  return (
    <div className="max-w-[1400px] mx-auto px-8 py-10">
      <div className="border-b border-border pb-6 mb-6">
        <button onClick={onBack} className="inline-flex items-center gap-2 text-xs font-mono-data uppercase tracking-widest text-muted-foreground hover:text-foreground mb-5">
          <ArrowLeft size={14} weight="bold" /> Voltar às provas
        </button>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-5">
          <div>
            <div className="label-eyebrow mb-2">prova · área de trabalho</div>
            <h1 className="font-display text-5xl font-black tracking-tighter leading-none">{exam.name}</h1>
            <div className="text-sm text-muted-foreground font-mono-data mt-3">
              {classNames.join(" · ")} · {semesterName} · {exam.exam_date ? `realizada em ${new Date(`${exam.exam_date}T12:00:00`).toLocaleDateString("pt-BR")} · ` : ""}{totalValid} questão(ões) válidas · {exam.question_source === "question_bank" ? "Banco de questões" : "Gabarito manual"}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canWrite && (exam.question_source !== "question_bank" || exam.can_edit_question_bank_key || exam.can_edit_imported_key) && <Button variant="outline" onClick={onEdit} className="rounded-none font-mono-data uppercase tracking-widest text-xs">
              <PencilSimple size={14} weight="bold" className="mr-2" /> Editar gabarito
            </Button>}
            {canSeeIndividualResults && <Button onClick={exportXlsx} className="rounded-none font-mono-data uppercase tracking-widest text-xs">
              <DownloadSimple size={14} weight="bold" className="mr-2" /> Relatório XLSX
            </Button>}
          </div>
        </div>
      </div>

      {canSeeGlobalResults && <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatBox label="cartões corrigidos" value={loading ? "…" : summary.cards || 0} hint={`${summary.linked_students || 0} vinculados a alunos`} />
        <StatBox label="média de acertos" value={loading ? "…" : (summary.average_hits || 0).toFixed(1)} hint={`de ${totalValid} válidas`} />
        <StatBox label="média da turma" value={loading ? "…" : `${(summary.average_score || 0).toFixed(1)}%`} hint="percentual de acertos" />
        <StatBox label="não identificados" value={loading ? "…" : summary.unidentified_cards || 0} hint="CPF não vinculado ao cadastro" />
      </div>}

      {!canWrite && <div className="border border-primary/40 bg-primary/5 p-4 mb-5 text-sm" data-testid="shared-exam-access-notice">
        Esta prova foi compartilhada com você em modo <b>{accessLevel === "individual" ? "resultados individuais" : accessLevel === "global" ? "resultados globais" : "somente prova"}</b>. Edição, envio e revisão de cartões permanecem com o criador e os gestores.
      </div>}

      {canWrite && <QuestionKeyChanges exam={exam} onExamChanged={onExamChanged} />}

      <div className="flex flex-wrap gap-2 mb-5">
        {canWrite && <button onClick={() => setActiveTab("upload")} className={`px-4 py-2 border text-xs font-mono-data uppercase tracking-widest inline-flex items-center gap-2 ${activeTab === "upload" ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"}`}>
          <UploadSimple size={14} weight="bold" /> Cartões
        </button>}
        {canWrite && <button onClick={() => setActiveTab("review")} className={`px-4 py-2 border text-xs font-mono-data uppercase tracking-widest inline-flex items-center gap-2 ${activeTab === "review" ? "bg-[hsl(var(--warning))] text-black border-[hsl(var(--warning))]" : "border-border hover:border-foreground"}`}>
          <ClipboardText size={14} weight="bold" /> Revisão{reviewCount ? ` (${reviewCount})` : ""}
        </button>}
        {canSeeIndividualResults && <button onClick={() => setActiveTab("results")} className={`px-4 py-2 border text-xs font-mono-data uppercase tracking-widest inline-flex items-center gap-2 ${activeTab === "results" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-foreground"}`}>
          <UsersThree size={14} weight="bold" /> Resultados
        </button>}
        {canSeeGlobalResults && <button onClick={() => setActiveTab("analytics")} className={`px-4 py-2 border text-xs font-mono-data uppercase tracking-widest inline-flex items-center gap-2 ${activeTab === "analytics" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-foreground"}`}>
          <ChartBar size={14} weight="bold" /> Gráficos
        </button>}
        {canSeeGlobalResults && exam.academic_scope === "global" && <button onClick={() => setActiveTab("global-performance")} className={`px-4 py-2 border text-xs font-mono-data uppercase tracking-widest inline-flex items-center gap-2 ${activeTab === "global-performance" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-foreground"}`}>
          <ChartBar size={14} weight="bold" /> Comparativo global
        </button>}
        {canSeeGlobalResults && <button onClick={() => setActiveTab("psychometrics")} className={`px-4 py-2 border text-xs font-mono-data uppercase tracking-widest inline-flex items-center gap-2 ${activeTab === "psychometrics" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-foreground"}`}>
          <ChartBar size={14} weight="bold" /> Psicometria
        </button>}
        {canSeeGlobalResults && <button onClick={() => setActiveTab("enamed")} className={`px-4 py-2 border text-xs font-mono-data uppercase tracking-widest inline-flex items-center gap-2 ${activeTab === "enamed" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-foreground"}`}>
          <ChartBar size={14} weight="bold" /> ENAMED
        </button>}
        {canSeeGlobalResults && <button onClick={() => setActiveTab("perception")} className={`px-4 py-2 border text-xs font-mono-data uppercase tracking-widest inline-flex items-center gap-2 ${activeTab === "perception" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-foreground"}`}>
          <CheckCircle size={14} weight="bold" /> Percepção
        </button>}
      </div>

      {activeTab === "upload" && <UploadPanel exam={exam} classOptions={classOptions} onFinished={afterUpload} />}
      {activeTab === "review" && (
        <div className="space-y-5">
          <ReviewActions exam={exam} onChanged={afterUpload} />
          <ReviewPanel
            exam={exam}
            refreshKey={reviewRefreshKey}
            onCountChange={setReviewCount}
            onResultsReload={loadResults}
            selectedIdentityCorrectionId={identityReviewTargetId}
          />
        </div>
      )}
      {activeTab === "results" && <ResultsPanel data={data} loading={loading} onReload={loadResults} canWrite={canWrite} onReviewIdentity={openIdentityReview} />}
      {activeTab === "analytics" && <AnalyticsPanel data={data} loading={loading} />}
      {activeTab === "global-performance" && exam.academic_scope === "global" && <GlobalExamPerformancePanel exam={exam} />}
      {activeTab === "psychometrics" && <PsychometricsPanel examId={exam.id} />}
      {activeTab === "enamed" && <EnamedProfilePanel examId={exam.id} embedded />}
      {activeTab === "perception" && <PerceptionPanel data={data} loading={loading} />}
      {activeTab === "overview" && <div className="border border-border bg-card p-10 text-center"><div className="font-display text-2xl font-black">Prova compartilhada</div><p className="text-sm text-muted-foreground mt-2">Você pode consultar os dados e arquivos da prova. O criador não liberou resultados.</p></div>}
    </div>
  );
}
